import { useCallback, useEffect, useState, type RefObject } from 'react'

// Hover-cursor tracking (pointer devices only): whether the cursor is over the waveform and its
// horizontal offset. `enabled` is false for a static waveform so it attaches no listeners. DS-local
// copy of the audio-player `useCursor` (the DS layer can't import feature hooks).
export const useWaveformCursor = (
    wrapperRef: RefObject<HTMLElement | null>,
    enabled: boolean,
): readonly [boolean, number] => {
    const [showCursor, setShowCursor] = useState(false)
    const [cursorOffsetX, setCursorOffsetX] = useState(-1)

    useEffect(() => {
        const el = wrapperRef.current
        if (!el || !enabled || window.ontouchstart !== undefined) {
            // Reset so a mid-hover interactive→static toggle can't leave the cursor stuck on.
            setShowCursor(false)
            setCursorOffsetX(-1)

            return undefined
        }

        const handleMouseEnter = () => setShowCursor(true)
        const handleMouseLeave = () => {
            setShowCursor(false)
            setCursorOffsetX(-1)
        }

        el.addEventListener('mouseenter', handleMouseEnter)
        el.addEventListener('mousemove', handleMouseEnter, { once: true })
        el.addEventListener('mouseleave', handleMouseLeave)

        return () => {
            el.removeEventListener('mouseenter', handleMouseEnter)
            el.removeEventListener('mousemove', handleMouseEnter)
            el.removeEventListener('mouseleave', handleMouseLeave)
        }
    }, [wrapperRef, enabled])

    const getCursorOffset = useCallback((event: MouseEvent) => {
        setCursorOffsetX(event.offsetX)
    }, [])

    useEffect(() => {
        const el = wrapperRef.current
        if (!el || !enabled || !showCursor) {
            return undefined
        }

        el.addEventListener('mousemove', getCursorOffset)

        return () => {
            el.removeEventListener('mousemove', getCursorOffset)
        }
    }, [enabled, showCursor, wrapperRef, getCursorOffset])

    return [showCursor, cursorOffsetX] as const
}
